import {
  Breadcrumbs as MantineBreadcrumbs,
  Anchor,
  Text,
  Group,
  rem,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { IconChevronRight, IconHome } from "@tabler/icons-react";
import { Link, useLocation } from "react-router-dom";
import { useMemo } from "react";

const useStyles = createStyles((theme) => ({
  root: {
    padding: `${theme.spacing.xs} 0`,
  },
  breadcrumb: {
    display: "flex",
    alignItems: "center",
    gap: rem(4),
  },
  link: {
    color: theme.colors.dark[2],
    textDecoration: "none",
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    transition: "color 150ms ease",
    "&:hover": {
      color: theme.colors.cyan[4],
    },
  },
  current: {
    color: theme.colors.dark[0],
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
  },
  separator: {
    color: theme.colors.dark[4],
    margin: `0 ${rem(4)}`,
  },
  homeIcon: {
    color: theme.colors.dark[2],
    transition: "color 150ms ease",
    "&:hover": {
      color: theme.colors.cyan[4],
    },
  },
}));

export interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  showHome?: boolean;
  homeLabel?: string;
  homePath?: string;
  className?: string;
}

const formatPathSegment = (segment: string): string => {
  // Handle common patterns
  if (
    segment.match(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    )
  ) {
    return "Details";
  }

  // Convert kebab-case or snake_case to Title Case
  return segment
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const Breadcrumbs = ({
  items,
  showHome = true,
  homeLabel = "Dashboard",
  homePath = "/",
  className,
}: BreadcrumbsProps) => {
  const { classes } = useStyles();
  const location = useLocation();

  const breadcrumbItems = useMemo(() => {
    if (items) {
      return items;
    }

    // Auto-generate from current path
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const generatedItems: BreadcrumbItem[] = [];

    let currentPath = "";
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === pathSegments.length - 1;

      generatedItems.push({
        label: formatPathSegment(segment),
        path: isLast ? undefined : currentPath,
      });
    });

    return generatedItems;
  }, [items, location.pathname]);

  const allItems: BreadcrumbItem[] = showHome
    ? [{ label: homeLabel, path: homePath, icon: <IconHome size={14} /> }]
    : [];

  allItems.push(...breadcrumbItems);

  return (
    <MantineBreadcrumbs
      className={`${classes.root} ${className ?? ""}`}
      separator={
        <IconChevronRight
          size={14}
          className={classes.separator}
          stroke={1.5}
        />
      }
    >
      {allItems.map((item, index) => {
        const isLast = index === allItems.length - 1;

        if (isLast || !item.path) {
          return (
            <Group key={index} gap={4} className={classes.breadcrumb}>
              {item.icon}
              <Text className={classes.current}>{item.label}</Text>
            </Group>
          );
        }

        return (
          <Anchor
            key={index}
            component={Link}
            to={item.path}
            className={classes.link}
          >
            <Group gap={4} className={classes.breadcrumb}>
              {item.icon && (
                <span className={classes.homeIcon}>{item.icon}</span>
              )}
              {item.label}
            </Group>
          </Anchor>
        );
      })}
    </MantineBreadcrumbs>
  );
};
